import { getEtagFromStateOrPersistedState } from '@ms/yammer-data/dist/domains/ecs/selectors';
import {
  configureFeedThreadCountsForPersistedExperiment,
  setFeedConfigForThreadCountExperiment,
} from '@ms/yammer-data/dist/domains/feedConfig/actions';
import type { Store } from '@ms/yammer-data/dist/state/types';
import {
  endAndReportPerformanceMeasure,
  reportBootstrapNavigationAndResourceEvents,
  reportError,
  setIsAppInHideableIframe,
  startMultiStepScenarioPerformanceMeasure,
  startPerformanceMeasure,
} from '@ms/yammer-telemetry';
import { isAuthRedirect, isMsalRedirectAuthenticator } from '@ms/yammer-web-authenticators';
import { loadIsExpectedError } from '@ms/yammer-web-support/dist/errors';
import {
  ExpectedFailureReason,
  ensureInitializedWithTelemetry,
  notifyAppExpectedFailure,
  notifyAppInitializationFailure,
  notifyAppInitializationLoaded,
} from '@ms/yammer-web-support/dist/metaOsHub';
import { getRingId } from '@ms/yammer-web-support/dist/metaOsHub/persistRingInfo';
import { setupAuthenticator } from 'bootstrap/auth';
import { setupTelemetry, userProvisioningErrors } from 'bootstrap/config';
import loadRenderer from 'bootstrap/entries/3-render';
import { noop } from 'lodash';
import { persistAppNameKey, persistConfig } from 'persistedState';
import { createStore } from 'state/createStore';
import type { YammerTeamsNativeState } from 'state/types';
import { updateClientContextWithHostMetadata } from 'teams/clientContext';
import { provideStoreForTabCaching, registerTabCaching } from 'teams/registerTabCaching';
import { subscribeTeamsToNextPageLifecycleEvent } from 'teams/teamsPageLifecycleEvents';

import {
  acquireYammerTokenWithTelemetry,
  addTelemetryMiddleware,
  configureSession,
  configureThemeSettings,
  lazyInvokePostRenderActions,
  lazyPreloadPageData,
  lazySetUpDiagnosticsDebugLogging,
  loadBootstrapNetworkTopicSettings,
  loadFeatureAccessSettings,
  loadTeamsContext,
  loadUserData,
  savePersistKey,
  setupCache,
  setupFeatureFlighting,
  setupUserLocale,
  storeBootstrapFailure,
  writeHostingCookies,
} from './bootstrapActions';
import { reportBootstrapCompleted, reportBootstrapStarted, setupLogging } from './bootstrapTelemetry';
import { isPermanentFailure } from './isPermanentFailure';
import { redirectToWebAppIfNecessary } from './redirection';

/* eslint-disable-next-line max-lines-per-function */ /* oxlint-disable-next-line eslint/max-statements */
const bootstrapApp = async (): Promise<void> => {
  const measure = startPerformanceMeasure('bootstrap');
  const scenario = startMultiStepScenarioPerformanceMeasure({ scenario: 'bootstrap' });

  let endedWithError = false;
  let endedWithExpectedError = false;
  let interrupted = false;
  let store: Store<YammerTeamsNativeState> | undefined;

  try {
    scenario.startStep(1, 'Setup Logging');
    setupLogging();
    scenario.endStep(1);

    scenario.startStep(2, 'Redirect to Web App If Necessary');
    const isRedirecting = await redirectToWebAppIfNecessary();
    if (isRedirecting) {
      interrupted = true;
      scenario.endStep(2);

      return;
    }
    scenario.endStep(2);

    scenario.startStep(3, 'Preload Renderer Bundle');
    const rendererPromise = loadRenderer();
    scenario.endStep(3);

    scenario.startStep(4, 'Setup Telemetry');
    await setupTelemetry();
    scenario.endStep(4);

    scenario.startStep(5, 'Await Teams API Initialization');
    await ensureInitializedWithTelemetry();
    scenario.endStep(5);

    scenario.startStep(6, 'Register Tab Caching');
    registerTabCaching();
    scenario.endStep(6);

    scenario.startStep(7, 'Create Store');
    store = createStore();
    const { dispatch, getState } = store;
    provideStoreForTabCaching(getState);
    scenario.endStep(7);

    scenario.startStep(8, 'Kick Off Load Teams Context');
    const loadTeamsContextPromise = loadTeamsContext();
    scenario.endStep(8);

    scenario.startStep(9, 'Save Persist Key');
    savePersistKey(dispatch);
    scenario.endStep(9);

    scenario.startStep(10, 'Await Load Teams Context');
    await loadTeamsContextPromise;
    scenario.endStep(10);

    scenario.startStep(11, 'Setup Cache');
    await setupCache();
    scenario.endStep(11);

    scenario.startStep(12, 'Kick Off Setup Authenticator');
    const authenticatorPromise = setupAuthenticator();
    scenario.endStep(12);

    scenario.startStep(13, 'Update Client Context With Host Metadata');
    updateClientContextWithHostMetadata();
    scenario.endStep(13);

    scenario.startStep(14, 'Configure Feed Thread Counts for Persisted Experiment');
    const feedConfigPromise = configureFeedThreadCountsForPersistedExperiment(dispatch, persistConfig.key);
    scenario.endStep(14);

    scenario.startStep(15, 'Await Setup Authenticator');
    const authenticator = await authenticatorPromise;
    scenario.endStep(15);

    scenario.startStep(16, 'Kick Off Acquire Yammer Token (Early, Non-MSAL)');
    let acquireYammerTokenWithTelemetryPromise: Promise<string> | undefined;
    if (!isMsalRedirectAuthenticator(authenticator)) {
      acquireYammerTokenWithTelemetryPromise = acquireYammerTokenWithTelemetry(true);
      acquireYammerTokenWithTelemetryPromise.catch(noop); // prevent unhandledrejection before await at Step 28
    }
    scenario.endStep(16);

    scenario.startStep(17, 'Configure Session');
    configureSession(dispatch);
    scenario.endStep(17);

    scenario.startStep(18, 'Add Telemetry Middleware');
    await addTelemetryMiddleware(getState);
    scenario.endStep(18);

    scenario.startStep(19, 'Load ECS Experiments');
    const featureFlightingPromise = setupFeatureFlighting(dispatch, getState()).then(async () => {
      scenario.endStep(19);

      scenario.startStep(20, 'Lazy Setup Diagnostics Debug Logging');
      lazySetUpDiagnosticsDebugLogging(getState);
      scenario.endStep(20);

      scenario.startStep(21, 'Set Feed Config For Thread Count Experiment');
      await feedConfigPromise;
      setFeedConfigForThreadCountExperiment(dispatch, getState);
      scenario.endStep(21);
    });

    scenario.startStep(22, 'Initialize MSAL Redirect Authenticator');
    if (isMsalRedirectAuthenticator(authenticator)) {
      await authenticator.initializeCurrentAccount();

      if (isAuthRedirect()) {
        interrupted = true;
        scenario.endStep(22);

        return;
      }
    }
    scenario.endStep(22);

    scenario.startStep(23, 'Report Bootstrap Started');
    reportBootstrapStarted();
    scenario.endStep(23);

    scenario.startStep(24, 'Kick Off Acquire Yammer Token (MSAL Fallback)');
    acquireYammerTokenWithTelemetryPromise =
      acquireYammerTokenWithTelemetryPromise ?? acquireYammerTokenWithTelemetry(true);
    scenario.endStep(24);

    Promise.all([featureFlightingPromise, acquireYammerTokenWithTelemetryPromise])
      .then(async () => {
        scenario.startStep(25, 'Load Feature Access Settings');
        await loadFeatureAccessSettings(dispatch, getState);
        scenario.endStep(25);
      })
      .catch(noop);

    scenario.startStep(26, 'Setup User Locale');
    setupUserLocale(dispatch);
    scenario.endStep(26);

    scenario.startStep(27, 'Configure Theme Settings');
    configureThemeSettings(dispatch);
    scenario.endStep(27);

    scenario.startStep(28, 'Await On Yammer Token');
    const yammerToken = await acquireYammerTokenWithTelemetryPromise;
    scenario.endStep(28);

    scenario.startStep(29, 'Write Hosting Cookies');
    writeHostingCookies(yammerToken);
    scenario.endStep(29);

    scenario.startStep(30, 'Kick Off CurrentUserClients');
    const userDataPromise = loadUserData(dispatch, getState());
    scenario.endStep(30);

    scenario.startStep(31, 'Kick Off Page Data Preload');
    lazyPreloadPageData(dispatch, getState);
    scenario.endStep(31);

    scenario.startStep(32, 'Load Bootstrap Network Topic Settings');
    loadBootstrapNetworkTopicSettings(dispatch, getState);
    scenario.endStep(32);

    scenario.startStep(33, 'Await ECS Experiments and CurrentUserClients');
    await Promise.all([featureFlightingPromise, userDataPromise]);
    scenario.endStep(33);

    scenario.startStep(34, 'Subscribe Teams Lifecycle APIs to Page Load Events');
    setIsAppInHideableIframe(true);
    subscribeTeamsToNextPageLifecycleEvent();
    scenario.endStep(34);

    scenario.startStep(35, 'Get ETag From State or Persisted State');
    const etag = await getEtagFromStateOrPersistedState(getState());
    scenario.endStep(35);

    scenario.startStep(36, 'Await Renderer Bundle');
    const render = await rendererPromise;
    scenario.endStep(36);

    scenario.startStep(37, 'Render App Component');
    render({ store, target: document.getElementById('root') as HTMLElement, etag });
    scenario.endStep(37);

    scenario.startStep(38, 'Notify App Initialization Loaded');
    notifyAppInitializationLoaded();
    scenario.endStep(38);

    scenario.startStep(39, 'Kick Off Lazy Post-Render Actions');
    void lazyInvokePostRenderActions(getState);
    scenario.endStep(39);

    scenario.startStep(40, 'Report Bootstrap Completed');
    reportBootstrapCompleted();
    scenario.endStep(40);
  } catch (error: unknown) {
    endedWithError = true;
    const { isExpectedError } = await loadIsExpectedError();
    endedWithExpectedError = isExpectedError(error);

    const ringId = getRingId(persistAppNameKey);
    reportError({ error, eventProperties: { errorCode: 'BootstrapFailed', ...(ringId && { ringId }) } });

    // istanbul ignore else
    if (store) {
      storeBootstrapFailure(store.dispatch, error);
    }

    if (isPermanentFailure(error) || endedWithExpectedError) {
      notifyAppExpectedFailure(error, [userProvisioningErrors, ExpectedFailureReason.PermissionError]);
      // istanbul ignore else
      if (store) {
        const render = await loadRenderer();
        const etag = await getEtagFromStateOrPersistedState(store.getState());
        render({ store, target: document.getElementById('root') as HTMLElement, error, etag });
      }
    } else {
      notifyAppInitializationFailure(error);
    }
  } finally {
    scenario.startStep(41, 'End Bootstrap Performance Measure');
    endAndReportPerformanceMeasure({
      ...measure,
      eventProperties: {
        ...measure.eventProperties,
        endedWithError,
        interrupted,
        isExpectedError: endedWithExpectedError,
      },
    });
    scenario.endStep(41);

    scenario.startStep(42, 'Report Bootstrap Navigation and Resource Events');
    reportBootstrapNavigationAndResourceEvents();
    scenario.endStep(42);

    scenario.endAndReport({ endedWithError, interrupted });
  }
};

export default bootstrapApp;
